import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef
} from 'react'
import SideBar from '../components/Shared/SideBar/SideBar.component'
import Socket from '../components/Shared/Socket/Socket.component'
import KioskWaiting from '../components/Shared/KioskWaiting/KioskWaiting.component'
import { useTranslation } from 'react-i18next'
import { pathNameMap } from '../utils/constants'
import IncomingCall from '../components/Shared/IncomingCall/IncomingCall.component'
import Cookies from 'universal-cookie'
import { v4 as uuidv4 } from 'uuid'
import { useNavigate, useLocation } from 'react-router-dom'
import AppRoutes from './AppRoutes'
import { SideBarContextProvider } from '../context/Shared/SideBar.context'
import { CurrentUserContext } from '../context/Shared/CurrentUser.context'
import { CurrentPortalContext } from '../context/Shared/CurrentPortal.context'
import { KioskModeContext } from '../context/Shared/KioskMode.context'
import { trackEvent, getSortedPortals, routify } from '../utils/functions'
import { NotificationContext } from '../context/Shared/Notification.context'
import { theme } from '../styles/theme.style'
import {
  ChatSocketContextProvider,
  ChatSocketContext
} from '../context/ChatSocket/ChatSocket.context'
import { SignOutContextProvider } from '../context/Shared/SignOut.context'
import RoleSwitcher from '../components/Shared/RoleSwitcher/RoleSwitcher.component'

// todo: remove incomingcall and incomingaddtocall. One is duplicate.
// todo: move socket functions into socket component

export default function AppRouter () {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()

  const { me, portals } = useContext(CurrentUserContext)
  const { kioskMode } = useContext(KioskModeContext)
  const [socket, _setSocket] = useState(null)
  const [onlineStatus, _setOnlineStatus] = useState(false)
  const [online, _setOnline] = useState({})
  const [incomingCall, _setIncomingCall] = useState(false)
  const [incomingAddToCall, _setIncomingAddToCall] = useState(false)
  const [callFrom, _setCallFrom] = useState('')
  const { portal, setPortal, currentPortal, handlePortalChange } =
    useContext(CurrentPortalContext)
  const [userBeingCalledId, _setUserBeingCalledId] = useState(null)
  const { setSnackBarMessage } = useContext(NotificationContext)

  // Ref to store chat socket disconnect function
  const chatSocketDisconnectRef = useRef(null)

  // Use ref for socket to prevent callbacks from recreating when socket changes
  const socketRef = useRef()
  socketRef.current = socket

  // Memoize setState functions to prevent unnecessary re-renders
  const setIncomingCall = useCallback((value) => _setIncomingCall(value), [])
  const setIncomingAddToCall = useCallback(
    (value) => _setIncomingAddToCall(value),
    []
  )
  const setCallFrom = useCallback((value) => _setCallFrom(value), [])
  const setUserBeingCalledId = useCallback(
    (value) => _setUserBeingCalledId(value),
    []
  )
  const setOnline = useCallback((value) => _setOnline(value), [])
  const setOnlineStatus = useCallback((value) => _setOnlineStatus(value), [])

  useEffect(() => {
    if (me != null && portals != null) {
      const sortedUserPortals = getSortedPortals(me.portals)
      const allPortals = portals

      // if no portal selected, select first portal
      if (!currentPortal) {
        const firstPortal = sortedUserPortals[0]
        handlePortalChange(firstPortal.Portal.id)
        return
      }

      const currentPortalId = Number(currentPortal)

      // selected portal by portal id
      const selectedPortal = allPortals.filter(
        (portal) => portal.id === currentPortalId
      )

      // check if user has explicit permission on selected portal
      const foundUserPortal = sortedUserPortals.filter(
        (sortedPortal) => sortedPortal.portalId === currentPortalId
      )

      // if admin, set as admin (or persisted role override) regardless
      if (me.admin) {
        const adminCookies = new Cookies()
        const savedRole = adminCookies.get('adminRoleOverride') || 'Admin'
        if (selectedPortal.length > 0) {
          setPortal({
            name: selectedPortal[0].name,
            role: savedRole,
            hasIseeLicence: true,
            hasFlowVULicence: true,
            id: currentPortalId,
            bucket: selectedPortal[0].bucket,
            bucketLocation: selectedPortal[0].bucketLocation,
            mainPortalId: selectedPortal[0].mainPortalId,
            recordByDefault: selectedPortal[0].recordByDefault,
            neverRecord: selectedPortal[0].neverRecord
          })
        } else {
          setPortal({
            name: sortedUserPortals[0].Portal.name,
            role: savedRole,
            hasIseeLicence: true,
            hasFlowVULicence: true,
            id: currentPortalId,
            bucket: sortedUserPortals[0].Portal.bucket,
            bucketLocation: sortedUserPortals[0].Portal.bucketLocation,
            mainPortalId: sortedUserPortals[0].Portal.mainPortalId,
            recordByDefault: sortedUserPortals[0].Portal.recordByDefault,
            neverRecord: sortedUserPortals[0].Portal.neverRecord
          })
        }
        return
      }
      // user has explict permission on selected portal
      if (foundUserPortal.length > 0) {
        // portal found
        setPortal({
          name: foundUserPortal[0].Portal.name,
          role: foundUserPortal[0].role,
          hasIseeLicence: foundUserPortal[0].hasIseeLicence,
          hasFlowVULicence: foundUserPortal[0].hasFlowVULicence,
          id: currentPortalId,
          bucket: foundUserPortal[0].Portal.bucket,
          bucketLocation: foundUserPortal[0].Portal.bucketLocation,
          mainPortalId: foundUserPortal[0].Portal.mainPortalId,
          createdAt: foundUserPortal[0].Portal.createdAt,
          recordByDefault: foundUserPortal[0].Portal.recordByDefault,
          neverRecord: foundUserPortal[0].Portal.neverRecord
        })
      } else {
        // was subportal and not directly associated
        if (selectedPortal.length > 0) {
          const mainPortal = sortedUserPortals.filter(
            (sortedPortal) =>
              sortedPortal.portalId === selectedPortal[0].mainPortalId
          )
          if (mainPortal.length > 0) {
            setPortal({
              name: selectedPortal[0].name,
              role: mainPortal[0].role,
              hasIseeLicence: mainPortal[0].hasIseeLicence,
              hasFlowVULicence: mainPortal[0].hasFlowVULicence,
              id: currentPortalId,
              bucket: selectedPortal[0].bucket,
              bucketLocation: selectedPortal[0].bucketLocation,
              mainPortalId: selectedPortal[0].mainPortalId,
              createdAt: selectedPortal[0].createdAt,
              recordByDefault: selectedPortal[0].recordByDefault,
              neverRecord: selectedPortal[0].neverRecord
            })
          }
        } else {
          const firstPortal = sortedUserPortals[0]
          handlePortalChange(firstPortal.Portal.id)
        }
      }
    }
  }, [me, portals, currentPortal])

  useEffect(() => {
    return () => {
      disconnectSocket()
    }
  }, [])

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect()
    }
  }, [])

  const setSocket = useCallback(
    (mysocket) => {
      disconnectSocket()
      _setSocket(mysocket)
    },
    [disconnectSocket]
  )

  const goToSession = useCallback(
    (id) => {
      navigate(`/${routify(theme.SESSION)}/?join_session_id=${id}`)
    },
    [navigate]
  )

  const signOut = useCallback(async () => {
    try {
      // Disconnect main socket
      disconnectSocket()
      // Disconnect chat socket if available
      if (chatSocketDisconnectRef.current) {
        chatSocketDisconnectRef.current()
      }
      navigate('/' + routify(theme.LOGOUT))
    } catch (error) {
      console.error(error)
    }
  }, [disconnectSocket, navigate])

  // Check if we're in a session
  const isInSession = pathNameMap[location.pathname] === theme.SESSION

  const acceptCall = useCallback(
    (to) => {
      if (isInSession) {
        setSnackBarMessage(t('You are already in an active session'), 'warning')
        return
      }

      trackEvent('Call Accepted', {
        portal: portal.name,
        role: portal.role
      })

      const id = uuidv4()
      goToSession(id)
      socketRef.current?.emit('accept call', {
        id,
        to
      })
    },
    [portal.name, portal.role, goToSession, isInSession, setSnackBarMessage, t]
  )

  const rejectCall = useCallback(
    (to) => {
      trackEvent('Call Rejected', {
        portal: portal.name,
        role: portal.role
      })

      socketRef.current?.emit('reject call', {
        to
      })
    },
    [portal.name, portal.role]
  )

  const joinCall = useCallback(
    (id) => {
      trackEvent('Call Joined', {
        portal: portal.name,
        role: portal.role
      })

      if (pathNameMap[location.pathname] !== theme.SESSION) {
        setTimeout(() => {
          goToSession(id)
        }, 2000)
      }
    },
    [portal.name, portal.role, location.pathname, goToSession]
  )

  const addRemoteVUToCall = useCallback(
    (to, id) => {
      trackEvent('Add User To Call', {
        portal: portal.name,
        role: portal.role
      })
      setSnackBarMessage(t('Adding user to call'), 'info')
      socketRef.current?.emit('add to call', {
        id,
        to
      })
    },
    [portal.name, portal.role, setSnackBarMessage, t]
  )

  const addGlassVUToCall = useCallback(
    (to, id) => {
      trackEvent('Add Glass To Call', {
        portal: portal.name,
        role: portal.role
      })
      setSnackBarMessage(t('Adding glass to call'), 'info')
      socketRef.current?.emit('accept call', {
        id,
        to
      })
    },
    [portal.name, portal.role, setSnackBarMessage, t]
  )

  const initiateCall = useCallback(
    (to) => {
      if (isInSession) {
        setSnackBarMessage(t('You are already in an active session'), 'warning')
        return
      }

      trackEvent('Call Initiated', {
        portal: portal.name,
        role: portal.role
      })

      setSnackBarMessage(t('Call initiated'), 'info')
      setUserBeingCalledId(to)

      socketRef.current?.emit('initiate call', {
        to
      })
    },
    [portal.name, portal.role, setUserBeingCalledId, setSnackBarMessage, t, isInSession]
  )

  const setStatusAsBusy = useCallback(() => {
    socketRef.current?.emit('status busy')
  }, [])

  const setStatusAsFree = useCallback(() => {
    socketRef.current?.emit('status free')
  }, [])

  useEffect(() => {
    if (pathNameMap[location.pathname] !== theme.SESSION) {
      setStatusAsFree()
    } else {
      setStatusAsBusy()
    }
  }, [location.pathname])

  // Component to connect chat socket disconnect to signOut
  const ChatSocketDisconnectHandler = ({ disconnectRef }) => {
    const chatSocketContext = useContext(ChatSocketContext)

    useEffect(() => {
      if (chatSocketContext?.disconnectSocket) {
        disconnectRef.current = chatSocketContext.disconnectSocket
      }
      return () => {
        disconnectRef.current = null
      }
    }, [chatSocketContext, disconnectRef])

    return null
  }

  // Roles that are allowed to join calls
  const isCallRole = ['Super User', 'User', 'Viewer', 'Guest', 'Education'].includes(portal.role)

  // Auto-reject incoming calls for non-call roles (e.g. Manager) with a message
  useEffect(() => {
    if (!isCallRole && incomingCall && callFrom) {
      setSnackBarMessage(t('Managers are not allowed to join sessions'), 'error')
      rejectCall(callFrom.from?.socketId)
      _setIncomingCall(false)
    }
  }, [incomingCall, isCallRole, callFrom])

  useEffect(() => {
    if (!isCallRole && incomingAddToCall && callFrom) {
      setSnackBarMessage(t('Managers are not allowed to join sessions'), 'error')
      rejectCall(callFrom.from?.socketId)
      _setIncomingAddToCall(false)
    }
  }, [incomingAddToCall, isCallRole, callFrom])

  return me && portal.name && portal.role
    ? (
    <ChatSocketContextProvider userId={me.id} portalId={portal.id}>
      <ChatSocketDisconnectHandler disconnectRef={chatSocketDisconnectRef} />
      <Socket
        socket={socket}
        setSocket={setSocket}
        setOnline={setOnline}
        setOnlineStatus={setOnlineStatus}
        setIncomingCall={setIncomingCall}
        setCallFrom={setCallFrom}
        setIncomingAddToCall={setIncomingAddToCall}
        goToSession={goToSession}
        signOut={signOut}
        pathNameMap={pathNameMap}
        location={location}
        setUserBeingCalledId={setUserBeingCalledId}
      />
      <SignOutContextProvider signOut={signOut}>
        {/* Hide incoming call modals in kiosk mode (auto-accept) and for non-call roles (e.g. Manager) */}
        {!kioskMode && isCallRole && (
          <>
            <IncomingCall
              callFrom={callFrom}
              incomingCall={incomingCall}
              setIncomingCall={setIncomingCall}
              acceptCall={acceptCall}
              rejectCall={rejectCall}
              addFlag={false}
            />
            <IncomingCall
              callFrom={callFrom}
              incomingCall={incomingAddToCall}
              setIncomingCall={setIncomingAddToCall}
              acceptCall={joinCall}
              rejectCall={rejectCall}
              addFlag={true}
            />
          </>
        )}
        {/* Show kiosk waiting screen when in kiosk mode and not in a session */}
        {kioskMode && !isInSession && (
          <KioskWaiting onlineStatus={onlineStatus} />
        )}
        <RoleSwitcher />
        <SideBarContextProvider>
          <SideBar
            onlineStatus={onlineStatus}
            currentPortal={currentPortal}
            signOut={signOut}
          />
          {/* In kiosk mode, only render AppRoutes when in a session */}
          {(!kioskMode || isInSession) && (
            <AppRoutes
              online={online}
              initiateCall={initiateCall}
              userBeingCalledId={userBeingCalledId}
              addRemoteVUToCall={addRemoteVUToCall}
              addGlassVUToCall={addGlassVUToCall}
              signOut={signOut}
              joinCall={joinCall}
              setStatusAsBusy={setStatusAsBusy}
              setStatusAsFree={setStatusAsFree}
            />
          )}
        </SideBarContextProvider>
      </SignOutContextProvider>
    </ChatSocketContextProvider>
      )
    : null
}
